import { RoleType, RoleTypePriorityRank } from '@enums/role-type.enums';
import { DomainRole } from '@services/genesys-cloud-user.interface';

/**
 * Return the highest role rank from a list of roles
 * @param roles - list of roles to get the highest role rank from
 * @returns number indicating the highest role rank
 */
export function isValidRole(roleName: string): roleName is keyof typeof RoleTypePriorityRank {
  return roleName in RoleTypePriorityRank;
}

/**
 * TODO: Need to update
 * Return the highest role rank from a list of roles
 * @param roles - list of roles to get the highest role rank from
 * @returns number indicating the highest role rank
 */
export function getRoleRank(roleName: string): number {
  // Convert role names to match enum format
  const enumKey = roleName
    ?.toUpperCase()
    .replace(/ - /g, '_') // Handle "Communicate - Admin" case
    .replace(/ /g, '_') // Handle "Quality Administrator" case
    .replace(/^WEM_/, '') // Remove WEM_ prefix if exists
    .replace(/^GENESYS_CLOUD_/, ''); // Remove GENESYS_CLOUD_ prefix if exists

  // Map common variations to their enum keys
  const roleMapping: { [key: string]: string } = {
    SUPERVISOR: 'SUPERVISOR',
    QUALITY_ADMINISTRATOR: 'WEM_QUALITY_ADMINISTRATOR',
    COMMUNICATE_ADMIN: 'COMMUNICATE_ADMIN',
    OUTBOUND_ADMIN: 'OUTBOUND_ADMIN',
    MASTER_ADMIN: 'MASTER_ADMIN',
    ADMIN: 'ADMINISTRATOR',
    TELEPHONY_ADMIN: 'TELEPHONY_ADMIN',
    EMPLOYEE: 'DEVELOPER',
  };

  const mappedKey = roleMapping[enumKey] || enumKey;
  return isValidRole(mappedKey) ? RoleTypePriorityRank[mappedKey] : RoleTypePriorityRank.DEVELOPER;
}

/**
 * Return the highest role rank from a list of roles
 * @param roles - list of roles to get the highest role rank from
 * @returns number indicating the highest role rank
 */
export function getHighestRankedRole(roles: DomainRole[] | undefined): DomainRole | null {
  if (!roles || roles.length === 0) {
    return null;
  }

  return roles.reduce((highestRole, currentRole) => {
    const currentRank = getRoleRank(currentRole?.name);
    const highestRank = getRoleRank(highestRole?.name);
    return currentRank > highestRank ? currentRole : highestRole;
  }, roles[0]);
}

/**
 * Return boolean if role is a WEM admin role
 * @param role - role type to verify if it is a WEM admin role
 * @returns boolean indicating if the role is a WEM admin role
 */
export function isWEMAdmin(role: string): boolean {
  if (!role) {
    return false;
  }

  const normalizedRole = role.toLowerCase();
  return (
    normalizedRole === RoleType.WEM_QUALITY_ADMIN.toLowerCase() ||
    normalizedRole === RoleType.WEM_QUALITY_ADMINISTRATOR.toLowerCase() ||
    normalizedRole === RoleType.WEM_PERFORMANCE_MANAGEMENT_ADMIN.toLowerCase() ||
    normalizedRole === RoleType.WEM_PLANNER_ADMIN.toLowerCase() ||
    normalizedRole === RoleType.WEM_PLANNING_ADMIN.toLowerCase() ||
    normalizedRole === RoleType.SUPERVISOR.toLowerCase() ||
    normalizedRole === RoleType.MASTER_ADMIN.toLowerCase()
  );
}

/**
 * Return boolean if role is a standard non-WEM admin and not an Outbound Admin role
 * @param role - role type to verify if it is a standard non-WEM admin role
 * @returns boolean indicating if the role is a standard non-WEM admin role
 */
export function isDefaultAdmin(role: string): boolean {
  if (!role) {
    return false;
  }
  const normalizedRole = role.toLowerCase();
  return normalizedRole === RoleType.ADMINISTRATOR.toLowerCase() || normalizedRole === RoleType.ADMIN.toLowerCase();
}

/**
 * Return boolean if role is a standard Cloud Admin role
 * @param role - role type to verify if it is a standard Cloud Admin role
 * @returns boolean indicating if the role is a standard Cloud Admin role
 */
export function isCloudAdmin(role: string): boolean {
  if (!role) {
    return false;
  }
  const normalizedRole = role.toLowerCase();
  return (
    normalizedRole === RoleType.GENESYS_CLOUD_VOICE_ADMIN.toLowerCase() ||
    normalizedRole === RoleType.TELEPHONY_ADMIN.toLowerCase() ||
    normalizedRole === RoleType.COMMUNICATE_ADMIN.toLowerCase()
  );
}

/**
 * Return boolean if role is a standard Architect or System Administrator role
 * @param role - role type to verify if it is a standard Architect or System Administrator role
 * @returns boolean indicating if the role is a standard Architect or System Administrator role
 */
export function isSystemAdmin(role: string): boolean {
  if (!role) {
    return false;
  }
  const normalizedRole = role.toLowerCase();
  return (
    normalizedRole === RoleType.ARCHITECT.toLowerCase() ||
    normalizedRole === RoleType.SYSTEM_ADMINISTRATOR.toLowerCase()
  );
}

/**
 * Return boolean if role is a standard Outbound Admin role
 * @param role - role type to verify if it is a standard Outbound Admin role
 * @returns boolean indicating if the role is a standard Outbound Admin role
 */
export function isOutboundAdmin(role: string): boolean {
  if (!role) {
    return false;
  }
  const normalizedRole = role.toLowerCase();
  return normalizedRole === RoleType.OUTBOUND_ADMIN.toLowerCase();
}

/**
 * Return boolean if role is a standard Developer role
 * @param role - role type to verify if it is a standard Developer role
 * @returns boolean indicating if the role is a standard Developer role
 */
export function isDeveloper(role: string): boolean {
  if (!role) {
    return false;
  }
  const normalizedRole = role.toLowerCase();
  return normalizedRole === RoleType.DEVELOPER.toLowerCase();
}
